import React, { useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { useAuth } from '../../auth/useAuth';
import { UserProfile } from '../../auth/types';
import { trackReads } from '../../services/firebaseUsageTracker';

type UserWithStats = UserProfile & {
  lastActive?: Timestamp;
  invitedBy?: string;
};

type UserGroup = {
  casinoName: string;
  adminEmail?: string;
  users: UserWithStats[];
};

export const UserManagement: React.FC = () => {
  const { roles, profile } = useAuth();
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [transferTarget, setTransferTarget] = useState<string | null>(null);
  const [transferToAdmin, setTransferToAdmin] = useState('');
  const [admins, setAdmins] = useState<UserProfile[]>([]);

  const isDev = roles.includes('dev');
  const isAdmin = roles.includes('admin');

  useEffect(() => {
    if (!profile?.uid) return;

    // Devs see all users, admins see only users they invited
    const baseQuery = collection(db, 'users');
    
    // For admins, we need to query users where invitedBy equals their UID
    // Note: Firestore composite index may be needed for this query
    let q;
    if (isDev) {
      // Devs see all users
      q = query(baseQuery, orderBy('email'));
    } else if (isAdmin) {
      // Admins see users they invited (query without orderBy to avoid index requirement)
      q = query(baseQuery, where('invitedBy', '==', profile.uid));
    } else {
      // Non-admin shouldn't see this component, but fallback to empty
      setUsers([]);
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(q, snap => {
      trackReads('users', snap.docs.length, 'admin-users-listener');
      const mapped = snap.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          uid: docSnap.id,
          email: data.email ?? null,
          roles: data.roles ?? ['user'],
          invitedBy: data.invitedBy ?? null,
          disabled: data.disabled ?? false,
          casinoName: data.casinoName,
          createdAt: data.createdAt,
        } as UserWithStats;
      });
      // Sort client-side for admin (since we can't use orderBy with where on different field)
      const sorted = mapped.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
      setUsers(sorted);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching users:', error);
      setLoading(false);
    });

    return () => unsub();
  }, [profile?.uid, isDev, isAdmin]);

  // Load admins for transfer dropdown (dev only)
  useEffect(() => {
    if (!isDev) return;

    const q = query(
      collection(db, 'users'),
      where('roles', 'array-contains', 'admin')
    );

    const unsub = onSnapshot(q, snap => {
      const mapped = snap.docs.map(docSnap => ({
        uid: docSnap.id,
        email: docSnap.data().email ?? null,
        roles: docSnap.data().roles ?? [],
        disabled: docSnap.data().disabled ?? false,
      })) as UserProfile[];
      setAdmins(mapped.filter(a => !a.disabled));
    });

    return () => unsub();
  }, [isDev]);

  const disableUserFn = httpsCallable(functions, 'disableUser');
  const transferUserFn = httpsCallable(functions, 'transferUser');

  const handleToggleDisable = async (user: UserWithStats) => {
    setActionLoading(user.uid);
    setMessage(null);

    try {
      const result = await disableUserFn({
        targetUid: user.uid,
        disabled: !user.disabled,
      });
      const data = result.data as { cascadedUsers?: number };
      
      if (data.cascadedUsers && data.cascadedUsers > 0) {
        setMessage({
          type: 'success',
          text: `User ${user.disabled ? 'enabled' : 'disabled'}. ${data.cascadedUsers} group members also affected.`,
        });
      } else {
        setMessage({
          type: 'success',
          text: `User ${user.disabled ? 'enabled' : 'disabled'} successfully.`,
        });
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to update user.',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleTransfer = async (userUid: string) => {
    if (!transferToAdmin) return;
    
    setActionLoading(userUid);
    setMessage(null);

    try {
      await transferUserFn({
        targetUid: userUid,
        newAdminUid: transferToAdmin,
      });
      setMessage({ type: 'success', text: 'User transferred successfully.' });
      setTransferTarget(null);
      setTransferToAdmin('');
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to transfer user.',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Role', 'Status', 'Invited By', 'Created At'];
    const rows = users.map(u => [
      u.email || '',
      u.roles.join(', '),
      u.disabled ? 'Disabled' : 'Active',
      u.invitedBy || 'N/A',
      u.createdAt ? u.createdAt.toDate().toISOString() : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (ts?: Timestamp | null) => {
    if (!ts) return '—';
    try {
      return ts.toDate().toLocaleDateString();
    } catch {
      return '—';
    }
  };

  const activeUsers = users.filter(u => !u.disabled);
  const disabledUsers = users.filter(u => u.disabled);

  // Group users by admin/casino for devs
  const userGroups = useMemo<UserGroup[]>(() => {
    if (!isDev) {
      // For admins, show users they invited (excluding other admins who have their own section)
      const regularUsers = activeUsers.filter(u => !u.roles.includes('admin') && !u.roles.includes('dev'));
      return [{
        casinoName: profile?.casinoName || 'Your Users',
        adminEmail: profile?.email ?? undefined,
        users: regularUsers
      }];
    }

    // For devs, each admin gets their own section with their invited users beneath them
    const groupMap = new Map<string, UserGroup>();
    
    // First, identify all admins (users with admin role, excluding devs for their own section)
    const adminUsers = activeUsers.filter(u => u.roles.includes('admin') && !u.roles.includes('dev'));
    const devUsers = activeUsers.filter(u => u.roles.includes('dev'));
    
    // Create a group for each admin with their casino name
    adminUsers.forEach(admin => {
      groupMap.set(admin.uid, {
        casinoName: admin.casinoName || admin.email || 'Unknown Casino',
        adminEmail: admin.email ?? undefined,
        users: [] // Will be populated with users they invited
      });
    });
    
    // Now assign regular users (non-admin, non-dev) to their inviting admin's group
    const regularUsers = activeUsers.filter(u => !u.roles.includes('admin') && !u.roles.includes('dev'));
    
    regularUsers.forEach(user => {
      const invitedBy = user.invitedBy;
      
      if (invitedBy && groupMap.has(invitedBy)) {
        // User was invited by a known admin
        groupMap.get(invitedBy)!.users.push(user);
      } else if (invitedBy) {
        // User was invited by someone (maybe a dev or unknown admin)
        // Check if this is a dev
        const invitingDev = devUsers.find(d => d.uid === invitedBy);
        if (invitingDev) {
          // Create or add to dev's group
          if (!groupMap.has(invitedBy)) {
            groupMap.set(invitedBy, {
              casinoName: invitingDev.casinoName || invitingDev.email || 'Dev Users',
              adminEmail: invitingDev.email ?? undefined,
              users: []
            });
          }
          groupMap.get(invitedBy)!.users.push(user);
        } else {
          // Unknown inviter - put in unassigned
          if (!groupMap.has('unassigned')) {
            groupMap.set('unassigned', {
              casinoName: 'Unassigned Users',
              adminEmail: undefined,
              users: []
            });
          }
          groupMap.get('unassigned')!.users.push(user);
        }
      } else {
        // No invitedBy - unassigned
        if (!groupMap.has('unassigned')) {
          groupMap.set('unassigned', {
            casinoName: 'Unassigned Users',
            adminEmail: undefined,
            users: []
          });
        }
        groupMap.get('unassigned')!.users.push(user);
      }
    });
    
    // Add devs to unassigned group (they don't belong to any casino)
    devUsers.forEach(dev => {
      if (!groupMap.has('unassigned')) {
        groupMap.set('unassigned', {
          casinoName: 'Unassigned Users',
          adminEmail: undefined,
          users: []
        });
      }
      groupMap.get('unassigned')!.users.push(dev);
    });
    
    // Sort groups: admin casinos first (alphabetically), then unassigned last
    return Array.from(groupMap.values()).sort((a, b) => {
      if (a.casinoName === 'Unassigned Users') return 1;
      if (b.casinoName === 'Unassigned Users') return -1;
      return a.casinoName.localeCompare(b.casinoName);
    });
  }, [isDev, activeUsers, profile]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary and Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {isDev ? 'All Users' : 'Your Users'}
          </h3>
          <p className="text-sm text-gray-400">
            {activeUsers.length} active, {disabledUsers.length} disabled
          </p>
        </div>
        <button
          onClick={exportToCSV}
          className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium transition-colors"
        >
          Export CSV
        </button>
      </div>

      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-emerald-900/50 border border-emerald-700 text-emerald-200'
              : 'bg-red-900/50 border border-red-700 text-red-200'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Active Users - Grouped by Casino/Admin */}
      {isDev ? (
        // Dev view: show users grouped by admin/casino
        <div className="space-y-4">
          {userGroups.length === 0 ? (
      <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
              <p className="text-sm text-gray-400">No users found</p>
            </div>
          ) : (
            userGroups.map(group => (
              <div key={group.casinoName} className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-white flex items-center gap-2">
                      <span className="text-lg">🎰</span>
                      {group.casinoName}
                    </h4>
                    {group.adminEmail && (
                      <p className="text-xs text-gray-400">Admin: {group.adminEmail}</p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 bg-gray-900 px-2 py-1 rounded">
                    {group.users.length} user{group.users.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {group.users.length === 0 ? (
                  <p className="text-sm text-gray-500">No users in this group</p>
        ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {group.users.map(user => (
              <div
                key={user.uid}
                className="p-3 bg-gray-900/60 rounded-lg border border-gray-700"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white truncate">
                        {user.email || user.uid}
                      </span>
                      {user.roles.includes('admin') && (
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-700 text-purple-100">
                          Admin
                        </span>
                      )}
                      {user.roles.includes('dev') && (
                        <span className="px-2 py-0.5 text-xs rounded bg-blue-700 text-blue-100">
                          Dev
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      Joined: {formatDate(user.createdAt)}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-2">
                    {/* Transfer button (dev only) */}
                            {!user.roles.includes('dev') && (
                      <>
                        {transferTarget === user.uid ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={transferToAdmin}
                              onChange={e => setTransferToAdmin(e.target.value)}
                              className="px-2 py-1 text-xs rounded bg-gray-800 border border-gray-600 text-white"
                            >
                              <option value="">Select admin...</option>
                              {admins.map(admin => (
                                <option key={admin.uid} value={admin.uid}>
                                  {admin.email || admin.uid}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => handleTransfer(user.uid)}
                              disabled={!transferToAdmin || actionLoading === user.uid}
                              className="px-2 py-1 text-xs rounded bg-blue-700 hover:bg-blue-600 text-white disabled:opacity-60"
                            >
                              Go
                            </button>
                            <button
                              onClick={() => {
                                setTransferTarget(null);
                                setTransferToAdmin('');
                              }}
                              className="px-2 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 text-white"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setTransferTarget(user.uid)}
                            className="px-3 py-1.5 text-xs rounded bg-gray-700 hover:bg-gray-600 text-white"
                          >
                            Transfer
                          </button>
                        )}
                      </>
                    )}

                    {/* Disable button */}
                            <button
                              onClick={() => handleToggleDisable(user)}
                              disabled={actionLoading === user.uid}
                              className="px-3 py-1.5 text-xs rounded bg-amber-700 hover:bg-amber-600 text-white disabled:opacity-60"
                            >
                              {actionLoading === user.uid ? '...' : 'Disable'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        // Admin view: simple list of their users
        <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
          <h4 className="font-semibold text-white mb-3">Active Users ({activeUsers.length})</h4>
          
          {activeUsers.length === 0 ? (
            <p className="text-sm text-gray-400">No active users. Send an invite to get started!</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {activeUsers.map(user => (
                <div
                  key={user.uid}
                  className="p-3 bg-gray-900/60 rounded-lg border border-gray-700"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white truncate">
                          {user.email || user.uid}
                        </span>
                        {user.roles.includes('admin') && (
                          <span className="px-2 py-0.5 text-xs rounded bg-purple-700 text-purple-100">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Joined: {formatDate(user.createdAt)}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-2">
                      {/* Disable button - admins can only disable regular users */}
                      {!user.roles.includes('admin') && !user.roles.includes('dev') && (
                      <button
                        onClick={() => handleToggleDisable(user)}
                        disabled={actionLoading === user.uid}
                        className="px-3 py-1.5 text-xs rounded bg-amber-700 hover:bg-amber-600 text-white disabled:opacity-60"
                      >
                        {actionLoading === user.uid ? '...' : 'Disable'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Disabled Users */}
      {disabledUsers.length > 0 && (
        <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
          <h4 className="font-semibold text-white mb-3">Disabled Users ({disabledUsers.length})</h4>
          
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {disabledUsers.map(user => (
              <div
                key={user.uid}
                className="p-3 bg-gray-900/40 rounded-lg border border-gray-800 opacity-60"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-300 truncate">
                        {user.email || user.uid}
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded bg-red-900 text-red-300">
                        Disabled
                      </span>
                    </div>
                  </div>

                  {isDev && (
                    <button
                      onClick={() => handleToggleDisable(user)}
                      disabled={actionLoading === user.uid}
                      className="px-3 py-1.5 text-xs rounded bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-60"
                    >
                      {actionLoading === user.uid ? '...' : 'Enable'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

